import React, { useState, useEffect } from 'react';
import './TerminalLanding.css';

// Enhanced configuration with colors and special effects
const terminalConfig = {
    lines: [
        { text: '> initializing portfolio...', color: '#00ff41', delay: 200 },
        { text: '[████████████████████░░░░░░░░░░░░] 75%', color: '#00ff41', isProgress: true, delay: 300 },
        { text: '[██████████████████████████░░░░░░] 90%', color: '#00ff41', isProgress: true, delay: 400 },
        { text: '[████████████████████████████████] 100%', color: '#00ff41', isProgress: true, delay: 500 },
        { text: '> system ready ✓', color: '#00ff41' },
        { text: '', color: '#eaeaea' }, // empty line
        { text: '> hello, world', color: '#eaeaea' },
        { text: '...', color: '#666' },
        { text: '> i\'m Pragyanshu Tiwari', color: '#eaeaea' },
        { text: '...', color: '#666' },
        { text: '> full-stack developer | mern stack', color: '#eaeaea' },
        { text: '...', color: '#666' },
        { text: '> i build products, not just projects', color: '#eaeaea' },
        { text: '...', color: '#666' },
        { text: '> tap or press enter to continue', color: '#81c784', glow: true }
    ],
    typingSpeed: 40,
    pauseBetweenLines: 600,
    delayBeforeStart: 800
};

const TerminalLanding = ({ onEnterPress }) => {
    const [displayedLines, setDisplayedLines] = useState([]);
    const [currentLineIndex, setCurrentLineIndex] = useState(0);
    const [currentText, setCurrentText] = useState('');
    const [showCursor, setShowCursor] = useState(true);
    const [allLinesComplete, setAllLinesComplete] = useState(false);
    const [isTyping, setIsTyping] = useState(false);

    // Handle Enter key press
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'Enter' && allLinesComplete) {
                onEnterPress();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [allLinesComplete, onEnterPress]);

    const handleClick = () => {
        if (allLinesComplete) {
            onEnterPress();
        }
    };

    // Cursor blinking effect
    useEffect(() => {
        const cursorInterval = setInterval(() => {
            setShowCursor(prev => !prev);
        }, 530);

        return () => clearInterval(cursorInterval);
    }, []);

    // Enhanced typing logic with colors
    useEffect(() => {
        if (currentLineIndex >= terminalConfig.lines.length) {
            setAllLinesComplete(true);
            return;
        }

        const currentLine = terminalConfig.lines[currentLineIndex];

        // Progress bar should appear instantly, not typed
        if (currentLine.isProgress) {
            setDisplayedLines(prev => [...prev, currentLine]);
            setCurrentText('');
            setIsTyping(false);

            setTimeout(() => {
                setCurrentLineIndex(prev => prev + 1);
            }, currentLine.delay || terminalConfig.pauseBetweenLines);
            return;
        }

        let charIndex = 0;
        setIsTyping(true);
        setCurrentText('');

        const typeChar = () => {
            if (charIndex < currentLine.text.length) {
                setCurrentText(currentLine.text.substring(0, charIndex + 1));
                charIndex++;
                setTimeout(typeChar, terminalConfig.typingSpeed + Math.random() * 20);
            } else {
                // Line complete, add to displayed lines
                setDisplayedLines(prev => [...prev, currentLine]);
                setCurrentText('');
                setIsTyping(false);

                // Move to next line after pause
                setTimeout(() => {
                    setCurrentLineIndex(prev => prev + 1);
                }, currentLine.delay || terminalConfig.pauseBetweenLines);
            }
        };

        // Start typing after initial delay (only for first line)
        const delay = currentLineIndex === 0 ? terminalConfig.delayBeforeStart : 0;
        setTimeout(typeChar, delay);
    }, [currentLineIndex]);

    return (
        <div className="terminal-container" onClick={handleClick} style={{ cursor: allLinesComplete ? 'pointer' : 'default' }}>
            <div className="terminal-content">
                <div className="terminal-header">
                    <div className="terminal-buttons">
                        <span className="terminal-button close"></span>
                        <span className="terminal-button minimize"></span>
                        <span className="terminal-button maximize"></span>
                    </div>
                    <div className="terminal-title">
                        <span className="terminal-user">pragyanshu@portfolio</span>
                        <span className="terminal-path">:~$</span>
                    </div>
                </div>

                <div className="terminal-body">
                    <div className="terminal-text">
                        {/* Display completed lines */}
                        {displayedLines.map((line, index) => {
                            // For progress bars, only show the latest one
                            if (line.isProgress) {
                                const laterProgressExists = displayedLines.slice(index + 1).some(laterLine => laterLine.isProgress);
                                if (laterProgressExists) {
                                    return null; // Hide this progress bar if a later one exists
                                }
                            }

                            return (
                                <div
                                    key={index}
                                    className={`terminal-line ${line.glow ? 'glow-text' : ''} ${line.isProgress ? 'progress-bar' : ''}`}
                                    style={{ color: line.color }}
                                >
                                    {line.text}
                                </div>
                            );
                        })}

                        {/* Display current typing line */}
                        {currentText && (
                            <div
                                className="terminal-line current-line"
                                style={{ color: terminalConfig.lines[currentLineIndex]?.color }}
                            >
                                {currentText}
                                {isTyping && <span className="typing-cursor">█</span>}
                            </div>
                        )}

                        {/* Show blinking cursor when all done */}
                        {allLinesComplete && (
                            <span className={`cursor ${showCursor ? 'visible' : 'hidden'}`}>
                                █
                            </span>
                        )}
                    </div>

                    {allLinesComplete && (
                        <div className="enter-hint">
                            <span className="hint-text pulse-glow">Tap or press ENTER to continue...</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TerminalLanding;